/**
 * Contextual internal links for maintenance services.
 *
 * Every service in a maintenance milestone has a spec page on this site that
 * an owner actually needs before doing the job: tire sizes before a rotation,
 * bolt pattern and torque before pulling a wheel, transmission type before
 * buying fluid, engine specs before buying plugs or filters. Surfacing those
 * links inside the timeline keeps people moving between pages instead of
 * bouncing back to search.
 */

export type ServiceLink =
  | { kind: "vehicle"; dataType: string; label: string; why: string }
  | { kind: "tool"; slug: string; label: string; why: string }
  | { kind: "model"; label: string; why: string };

export interface LinkContext {
  /** Spec types present in the database for this model (tire-size, etc.). */
  availableSpecTypes?: string[];
  modelName?: string;
}

const V = (dataType: string, label: string, why: string): ServiceLink => ({
  kind: "vehicle",
  dataType,
  label,
  why,
});
const T = (slug: string, label: string, why: string): ServiceLink => ({
  kind: "tool",
  slug,
  label,
  why,
});

/** Links keyed by exact service name, before availability filtering. */
function rulesFor(service: string, category: string): ServiceLink[] {
  const s = service.toLowerCase();

  if (s.includes("tire rotation")) {
    return [
      V("tire-size", "Tire sizes", "OEM front, rear and spare sizes plus placard pressures"),
      V("bolt-pattern", "Bolt pattern & lug torque", "PCD, thread size and torque spec for refitting wheels"),
      V("drivetrain", "Drivetrain", "AWD and 4WD models need matched tread depth across all four corners"),
      V("vehicle-class", "Vehicle class & weight", "Class and curb weight set the load rating your tires need"),
    ];
  }
  if (s.includes("brake pad") || s.includes("brake fluid")) {
    return [
      V("tire-size", "Wheel & tire sizes", "Wheel diameter decides which caliper and rotor package fits"),
      V("towing-capacity", "Towing capacity", "Braking load rises sharply when you tow near the limit"),
      V("vehicle-class", "Vehicle class & weight", "Curb weight and class drive pad and rotor wear rates"),
    ];
  }
  if (s.includes("engine oil")) {
    return [
      V("engine", "Engine specs", "Cylinders and displacement determine oil grade and capacity"),
      V("mpg", "MPG by year", "Fresh oil and a clean filter protect rated fuel economy"),
      T("fuel-cost-calculator", "Fuel cost calculator", "See what this engine costs to run each year"),
    ];
  }
  if (s.includes("spark plug") || s.includes("belt") || s.includes("coolant") && !s.includes("battery")) {
    return [
      V("engine", "Engine specs", "Plug count, belt routing and coolant volume follow the engine"),
      V("transmission", "Transmission", "Confirm the powertrain combination fitted to your year"),
    ];
  }
  if (s.includes("cabin air filter")) {
    return [
      V("vehicle-class", "Vehicle class & size", "Cabin volume decides filter size and how often it loads up"),
      V("engine", "Engine specs", "Cross-check the exact year and trim before ordering"),
    ];
  }
  if (s.includes("air filter") || s.includes("fuel filter")) {
    return [
      V("engine", "Engine specs", "Filter part numbers are matched to engine and model year"),
      V("fuel-cost", "Annual fuel cost", "A restricted intake or fuel filter shows up at the pump"),
    ];
  }
  if (s.includes("transmission fluid") || s.includes("cvt fluid")) {
    return [
      V("transmission", "Transmission specs", "CVT, manual and torque-converter units take different fluid"),
      V("drivetrain", "Drivetrain", "Front, rear and all-wheel layouts change fill and drain points"),
    ];
  }
  if (s.includes("differential") || s.includes("transfer case") || s.includes("reduction gear")) {
    return [
      V("drivetrain", "Drivetrain specs", "Which axles and transfer case your version actually has"),
      V("towing-capacity", "Towing capacity", "Towing shortens gear-oil life on every driven axle"),
      V("transmission", "Transmission", "Gearbox pairing changes the service list"),
    ];
  }
  if (s.includes("battery") || s.includes("wiper") || category === "Electrical") {
    return [
      V("engine", "Powertrain specs", "12V, hybrid and EV systems each need a different battery service"),
      V("mpg", "MPG & range", "Weak batteries and worn accessories drag efficiency down"),
    ];
  }
  return [V("engine", "Engine specs", "Full powertrain detail for this model year")];
}

const ALWAYS_AVAILABLE = new Set([
  "mpg",
  "engine",
  "fuel-cost",
  "transmission",
  "drivetrain",
  "vehicle-class",
]);

/**
 * Contextual links for one service, filtered to pages that actually exist for
 * this model. Spec pages (tire size, bolt pattern, towing) are only published
 * when verified data is present.
 */
export function linksForService(
  service: string,
  category: string,
  ctx: LinkContext = {},
): ServiceLink[] {
  const available = new Set(ctx.availableSpecTypes ?? []);
  const links = rulesFor(service, category).filter((l) =>
    l.kind === "vehicle"
      ? ALWAYS_AVAILABLE.has(l.dataType) || available.has(l.dataType)
      : true,
  );
  if (!links.length) {
    return [V("engine", "Engine specs", "Full powertrain detail for this model year")];
  }
  return links.slice(0, 3);
}

/** De-duplicated links across every service due at a milestone. */
export function linksForMilestone(
  services: string[],
  categoryOf: (service: string) => string,
  ctx: LinkContext = {},
): ServiceLink[] {
  const seen = new Set<string>();
  const out: ServiceLink[] = [];
  for (const s of services) {
    for (const l of linksForService(s, categoryOf(s), ctx)) {
      const key = l.kind === "vehicle" ? `v:${l.dataType}` : l.kind === "tool" ? `t:${l.slug}` : "m";
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(l);
    }
  }
  return out.slice(0, 6);
}